import { useId, useState } from "react";

import type { TournamentWhatsappGroup } from "../lib/api";
import { playerWhatsappGroups, whatsappGroupChoiceLabel } from "../lib/tournamentExtras";
import Icon from "./icons";

/**
 * «Unirme al grupo de WhatsApp» del jugador inscrito (Eduardo 2026-10-01): en
 * la tarjeta del torneo de «Mis torneos» y en el inicio del torneo.
 *
 * - Sin grupos (o solo con links que no son de WhatsApp): no se pinta.
 * - Un grupo: el botón lo abre directo en otra pestaña (con su nombre si lo
 *   tiene: «Unirme a Papás Verde»).
 * - Varios: el botón despliega la lista para elegir («Grupo Roja», «Papás
 *   Verde»…). La lista va en la misma fila (ancho completo) para que la
 *   tarjeta, que recorta lo que sobresale, no la esconda.
 *
 * Con «Ver como jugador» se ve igual: son solo ligas, no cambian nada.
 */
export default function PlayerWhatsappGroupsButton({
  groups,
  className,
  lineIcon = false,
}: {
  groups: TournamentWhatsappGroup[] | null | undefined;
  className: string;
  /** Inicio del torneo (Eduardo 2026-10-05, maqueta aprobada): ícono de
   * línea en lugar del emoji 💬 y flecha que gira al abrir la lista. */
  lineIcon?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const valid = playerWhatsappGroups(groups);

  if (valid.length === 0) return null;

  const icon = lineIcon ? <Icon name="chat" /> : "💬 ";

  if (valid.length === 1) {
    const [group] = valid;
    // Con nombre, el botón lo dice («Unirme a Papás Verde»), igual que la
    // pantalla de después de inscribirse («Entrar a Papás Verde»): en el
    // celular no se ve el title.
    return (
      <a
        className={className}
        href={group.url}
        target="_blank"
        rel="noopener noreferrer"
        title={group.label ? `Grupo de WhatsApp: ${group.label}` : "Grupo de WhatsApp del torneo"}
      >
        {icon}
        {group.label ? (
          <>
            Unirme a {group.label}
            <span className="sr-only"> (grupo de WhatsApp)</span>
          </>
        ) : (
          "Unirme al grupo de WhatsApp"
        )}
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        className={className}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      >
        {icon}
        Unirme al grupo de WhatsApp{" "}
        {lineIcon ? (
          <Icon name="chevron-down" className={`player-whatsapp-groups__chevron${open ? " is-open" : ""}`} />
        ) : (
          <span aria-hidden="true">{open ? "▲" : "▼"}</span>
        )}
      </button>
      {open ? (
        <ul id={listId} className="player-whatsapp-groups" aria-label="Grupos de WhatsApp del torneo">
          {valid.map((group, index) => (
            <li key={`${group.url}-${index}`}>
              <a href={group.url} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
                <span>{whatsappGroupChoiceLabel(group, index)}</span>
                <span aria-hidden="true">↗</span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
